import apiClient from "#/redux/api/ApiClient";

import { apiFilter } from "./DataLayerFilter";
import { apiSort } from "./DataLayerSort";

function handleMutationResponse({ data }) {
  return data.output ?? data;
}

function handleUploadResponse({ data }) {
  return data;
}

/**
 * Processes and normalizes an API error object for consistent error handling.
 *
 * @param {Object} err - The error object typically returned from an API call.
 * @param {Object} [err.response] - The HTTP response associated with the error.
 * @param {number} [err.response.status] - The HTTP status code of the error.
 * @param {Object} [err.response.data] - The response body containing error details.
 * @param {string} [err.response.data.resultMessages] - A detailed error message from the API response.
 * @param {string} [err.message] - The default message describing the error.
 * @return {void} The method throws a normalized error object instead of returning it.
 */
export function throwNormalizedApiError(err) {
  const { status, data } = err.response || {};
  const error = {
    ...data,
    status,
    message: data?.resultMessages || err.message,
    response: err.response,
  };

  throw error;
}

/**
 * Generic POKK service API interface
 *
 * This class provides a generic interface for performing CRUD operations on a POKK resource.
 * It abstracts away the details of the API endpoints and allows for easy integration with different resources by simply providing a base URL.
 *
 * The class includes methods for querying, reading, creating, updating, and deleting resources, all of which interact with the provided API client.
 *
 * @param {object} param
 * @param {string} param.baseUrl - The base URL for a specific POKK resource, which will be used to construct the API endpoints for CRUD operations.
 * @param {object} param.client - An instance of an API client to handle HTTP requests. Defaults to a generic API client if not provided.
 *
 * @returns {object} An instance of the GenericPokkService class with methods for interacting with the specified POKK resource.
 *
 * @example
 * const horseService = new GenericPokkService({ baseUrl: "horses" });
 */
export default class GenericPokkService {
  baseUrl = undefined;

  /**
   * @var {ApiClient} client - An instance of an API client to handle HTTP requests.
   */
  client = undefined;

  /**
   * By default, the service will use the "/api/{baseUrl}/page" endpoint for queries, which supports pagination and filtering.
   * If your POKK resource does not support this endpoint, you can set usePageEndpoint to false,
   * and it will use the "/api/{baseUrl}" endpoint for queries instead.
   *
   * It also expects the response format to be { content: [...], total: number } when using the page endpoint, and will return the data accordingly.
   * If usePageEndpoint is false, it will expect the response to be a simple array of items.
   *
   * @var {boolean} usePageEndpoint - Whether to use the "/page" endpoint for queries. Defaults to true.
   */
  usePageEndpoint = true;

  constructor({ client = apiClient, ...params } = {}) {
    this.client = client;
    for (const [key, value] of Object.entries(params)) {
      if (key in this) {
        this[key] = value;
      }
    }
  }

  /**
   * Constructs the API endpoint URL based on the baseUrl and any additional parts provided.
   *
   * @param  {...any} parts
   * @returns string - The constructed API endpoint URL.
   */
  #endpoint(...parts) {
    return [this.baseUrl, ...parts.filter(Boolean)].join("/");
  }

  async query({ filter, sort, page, params } = {}) {
    return this.client
      .get(
        this.#endpoint(this.usePageEndpoint ? "page" : null),
        params,
        page,
        apiFilter(filter),
        apiSort(sort)
      )
      .then(({ data }) =>
        this.usePageEndpoint
          ? // pagination is default
            data
          : // if not using pagination, we expect a simple array of items,
            // so we wrap it in the expected format
            { content: data, total: data.length }
      )
      .catch(throwNormalizedApiError);
  }

  async read(id, params) {
    return this.client
      .get(this.#endpoint(id), params)
      .then(({ data }) => data)
      .catch(throwNormalizedApiError);
  }

  async create(payload = {}) {
    let endpoint = this.#endpoint();
    const body = { ...payload };

    if (payload.id && endpoint.includes("{id}")) {
      endpoint = endpoint.replace("{id}", payload.id);
      delete body.id;
    }

    return this.client
      .post(endpoint, body)
      .then(handleMutationResponse)
      .catch(throwNormalizedApiError);
  }

  async update(id, payload) {
    if (typeof id !== "string") {
      throw new Error("ID must be a string");
    }

    return this.client
      .put(this.#endpoint(id), payload)
      .then(handleMutationResponse)
      .catch(throwNormalizedApiError);
  }

  async delete(id, version) {
    if (typeof id !== "string") {
      throw new Error("ID must be a string");
    }

    if (typeof version !== "number") {
      throw new Error("Version must be a number");
    }

    return this.client
      .delete(this.#endpoint(id), { version })
      .then(handleMutationResponse)
      .catch(throwNormalizedApiError);
  }

  /**
   * Handles file uploads to the POKK service.
   *
   * @param {Blob} blob - The file blob to be uploaded.
   * @param {object} dto - Data transfer object associated with the file upload.
   * @returns {Promise<object>} - A promise that resolves with the response from the file upload.
   */
  async upload(blob, dto = {}) {
    return (
      this.client
        // for nowe we support only attachment upload, so we hardcode the endpoint to "uploadFile",
        // when we need to for uploads for other resources, (/api/dog/pripusti/pripust/upload)
        // we need to make this configurable (via $params
        .upload(this.#endpoint("uploadFile"), blob, dto)
        .then(handleUploadResponse)
        .catch(throwNormalizedApiError)
    );
  }

  // eslint-disable-next-line class-methods-use-this
  async sync() {
    // already synced
    return Promise.resolve();
  }
}
